import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Award, Users, Target, Heart, ArrowRight } from 'lucide-react'

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const values = [
    {
      icon: Heart,
      title: 'Patient Care First',
      description: 'Every innovation we develop is driven by our commitment to improving patient outcomes.'
    },
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for excellence in every product, service, and interaction with our customers.'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'We work closely with healthcare professionals to create solutions that truly meet their needs.'
    },
    {
      icon: Award,
      title: 'Innovation',
      description: 'Continuous research and development to push the boundaries of medical imaging technology.'
    },
  ]

  const team = [
    {
      name: 'Dr. Sarah Johnson',
      role: 'Chief Executive Officer',
      bio: '20+ years in medical technology leadership',
      avatar: 'SJ'
    },
    {
      name: 'Dr. Michael Chen',
      role: 'Chief Technology Officer',
      bio: 'Expert in ultrasound and imaging systems',
      avatar: 'MC'
    },
    {
      name: 'Dr. Emily Rodriguez',
      role: 'Head of Research & Development',
      bio: 'Leading innovation in medical imaging',
      avatar: 'ER'
    },
    {
      name: 'James Wilson',
      role: 'VP of Operations',
      bio: 'Ensuring quality and reliability',
      avatar: 'JW'
    },
  ]

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-100 to-gray-200 py-20">
        <div className="container-custom">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              About <span className="text-gradient">South Hampton</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Leading the future of medical imaging with innovative ultrasound and radiology technology 
              that empowers healthcare professionals worldwide.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                To advance medical imaging technology and improve healthcare outcomes by providing 
                cutting-edge ultrasound and radiology solutions that enable healthcare professionals 
                to deliver exceptional patient care.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Founded in 2013, South Hampton Medical Imaging has been at the forefront of medical 
                imaging innovation, serving over 1,000+ trusted patients with reliable, 
                advanced diagnostic equipment.
              </p>
              
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-gradient-to-br from-primary-500 to-accent-400 rounded-2xl p-8 text-white"
            >
              <h3 className="text-2xl font-bold mb-6">Our Impact</h3>
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="text-3xl font-bold mr-4">1,000+</div>
                  <div>
                    <div className="font-semibold">Trusted Patients</div>
                    <div className="text-sm opacity-90">Worldwide</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="text-3xl font-bold mr-4">13+</div>
                  <div>
                    <div className="font-semibold">Years of Experience</div>
                    <div className="text-sm opacity-90">Industry expertise</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="text-3xl font-bold mr-4">99.9%</div>
                  <div>
                    <div className="font-semibold">Uptime Guarantee</div>
                    <div className="text-sm opacity-90">Reliable performance</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-200">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do and every decision we make.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-accent-400 rounded-xl flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-20 bg-white" id="team">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the experts driving innovation in medical imaging technology.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-400 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-white font-bold text-xl">{member.avatar}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {member.name}
                  </h3>
                  <div className="text-primary-600 font-semibold mb-3">
                    {member.role}
                  </div>
                  <p className="text-gray-600 text-sm">
                    {member.bio}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}
    </div>
  )
}

export default About 